import React, { useMemo, useState } from 'react';
import { Table } from '@paretogroup/tess';
import { compose } from 'redux';
import { withCampaigns } from '@domain/campaigns';
import { withComparisonCampaigns } from '@domain/comparison-campaigns';
import { withUserConfig } from '@domain/user-config';

import { withObjectSelected } from '@domain/object-selected';
import { ALL_METRICS } from '@domain/campaigns/campaign-table/campaign-table-metrics.constants';
import { DEFAULT_SORT, DEFAULT_SORT_COMPARISON } from '@domain/campaigns/campaign-table/campaign-table.constants';
import { formatCampaignMetricsToTableColumns } from '@domain/campaigns/campaign-table/campaign-table-comparison-columns.utils';
import { withDashboardFilters } from '@domain/dashboard/dashboard-filters';

import {
    getFilteredData, getInitialMetrics, getMetricsByContentType, METRICS_WITH_PROJECTION,
} from '@shared/tables';
import { useStyles } from '@domain/advertising/tables/metrics-report-table/metrics-report-table.styles';
import { TableHeaderComponent } from '@shared/tables/components/table-header/table-header-component';
import LoadingSkeleton from '@shared/components/loading/LoadingSkeleton';

import {
    MetricsReportTableProps,
} from './metrics-report-table.types';

const MetricsReportTableImpl = ({
    data,
    objectSelected,
    dashboardFilters,
    isLoading,
    additionalColumns,
    headerProps,
    changeCampaignsFilter,
    campaignTableMetrics,
}: MetricsReportTableProps) => {
    const { ranges } = dashboardFilters;
    const { advancedMetrics, headerTexts, dataAppcuesIds } = headerProps;
    const classes = useStyles();
    const initialSelectedMetrics = getInitialMetrics(objectSelected);

    const [filterBySearch, setFilterBySearch] = useState<string>('');
    const [selectedMetrics, setSelectedMetrics] = useState(
        campaignTableMetrics || initialSelectedMetrics,
    );

    const handleChangeMetrics = (metric) => {
        setSelectedMetrics(metric);
        changeCampaignsFilter({
            campaignTableMetrics: metric,
        });
    };

    const onChangeSearchFilter = ({ target }) => {
        const { value } = target;
        const valueLowerCase = value.toLowerCase();
        setFilterBySearch(valueLowerCase);
    };
    const metricsFilteredByName = selectedMetrics.map(metric => ALL_METRICS[metric]);

    const consolidatedColumns = useMemo(
        () => formatCampaignMetricsToTableColumns([
            ...additionalColumns,
            ...metricsFilteredByName,
        ], ranges),
        [additionalColumns, metricsFilteredByName, ranges],
    );

    const memoizedData = useMemo(() => data, [data]);

    const filteredData = useMemo(
        () => getFilteredData(memoizedData, filterBySearch),
        [memoizedData, filterBySearch],
    );

    const metricsThatShouldBeDisplayedInMetricsSelector = getMetricsByContentType(
        objectSelected, METRICS_WITH_PROJECTION,
    );

    const defaultSort = !ranges?.comparisonDate
        ? DEFAULT_SORT
        : DEFAULT_SORT_COMPARISON;
    return (
        <div className={classes.tableContainer}>
            <TableHeaderComponent
                onChange={onChangeSearchFilter}
                searchText={filterBySearch}
                selectedMetrics={selectedMetrics}
                setSelectedMetrics={handleChangeMetrics}
                metrics={metricsThatShouldBeDisplayedInMetricsSelector}
                advancedMetrics={advancedMetrics}
                texts={headerTexts}
                dataAppcuesId={dataAppcuesIds.INSIGHT_HEADER}
            />
            { isLoading
                ? <LoadingSkeleton count={11} height="58px" />
                : (
                    <Table
                        data={filteredData}
                        columns={consolidatedColumns}
                        classes={classes}
                        displayTotal
                        displayPagination
                        defaultSortBy={defaultSort}
                    />
                )
            }
        </div>
    );
};

MetricsReportTableImpl.defaultProps = {
    additionalColumns: [],
};

export const MetricsReportTable = compose(
    withDashboardFilters,
    withObjectSelected,
    withCampaigns,
    withComparisonCampaigns,
    withUserConfig,
)(MetricsReportTableImpl);
